import { prompts } from "../components/env";

/**
 * Schedule row primary key for `/visit/:id` (PatientPage). Always the DB `id` (UUID), not EHR_ID.
 * Use when navigating to the patient visit page from schedules, results, or table row actions.
 */
export function getPatientPageVisitPathSegment(row) {
  return row?.id != null ? String(row.id) : null;
}

/**
 * First segment after /visit/ for **edit-document** URLs only: prefer EHR_ID when set, else row `id`.
 * Matches `/visit/{ehr_or_id}/edit-document/{documents.id}`.
 */
export function getVisitPathSegmentFromScheduleRow(row) {
  if (!row) return null;
  const ehrIdx = prompts.findIndex((p) => p[0] === "EHR_ID");
  if (ehrIdx >= 0) {
    const field = prompts[ehrIdx][0];
    const v = row[field];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return row.id;
}

/** True if the route param is a Postgres/UUID primary key (not an EHR / other id). */
export function isScheduleRowUuidParam(param) {
  if (!param || typeof param !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    param.trim(),
  );
}

/**
 * Where a row-click should navigate to open that row's chart, as
 * `{ to, state }` — or null when the row has no id to open.
 *
 * The chart page resolves its date and its schema from BOTH the query string
 * and router state (see MedStudentClinicPage: `?date=` / `state.date_`, and
 * `?schema=` / `state.rowData._schema` / `state._schema`). Callers that omit
 * them get today's date in the viewer's HOME schema — which, for a
 * cross-schema search hit, means the visit id isn't found at all and the page
 * silently opens a brand-new document dated today instead of the row that was
 * clicked.
 *
 * Both are sent belt-and-braces: the query string survives a refresh or a
 * copied link, router state survives a date containing characters a caller
 * forgot to encode.
 *
 * @param row       the schedule/notes-c row that was clicked
 * @param prefix    'clinic' or 'visit', per the viewer's role
 * @param dateHint  date to open on; defaults to the row's own `date_`
 */
export function buildVisitNavigation(row, prefix, dateHint) {
  const seg = getPatientPageVisitPathSegment(row);
  if (!seg) return null;

  // A patients_with_ehrid row is patient-level: it has no visit to open, and
  // its id is not a notes-c id, so /clinic/:id would find nothing and silently
  // open a blank document dated today. Send it to the patient chart instead —
  // the same place the roster page goes.
  if (row?._source === "roster") {
    const rosterSchema = row?._schema || "";
    return {
      to: `/patients/${seg}${rosterSchema ? `?schema=${encodeURIComponent(rosterSchema)}` : ""}`,
      state: { rowData: row, userInfo: row, _schema: rosterSchema || undefined },
    };
  }

  const date = dateHint || row?.date_ || "";
  const schema = row?._schema || "";
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (schema) params.set("schema", schema);
  const qs = params.toString();
  return {
    to: `/${prefix}/${seg}${qs ? `?${qs}` : ""}`,
    state: {
      rowData: row,
      userInfo: row,
      date_: date || undefined,
      _schema: schema || undefined,
    },
  };
}
